import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Card,
  ConfigProvider,
  Divider,
  Empty,
  Flex,
  Skeleton,
  Table,
  TableColumnType,
  TablePaginationConfig,
} from '@duxton-web/ui';

import './DashboardTable.css';

import { ColumnType, TableProps } from '@duxton-web/ui/lib/table';
import { produce } from 'immer';
import lodash, { debounce, isEqual } from 'lodash';
import { useLocalStorage } from 'react-use';

import { VARIABLE_PAGE_SIZE } from '../../../store/state/variables/actions';
import FilterDrawer, { Filter, Group } from '../FilterDrawer/FilterDrawer';
import TableCustomizer from './TableCustomizer';
import TableSearch from './TableSearch';

export interface DashboardColumn extends ColumnType<any> {
  hidden?: boolean;
  description?: string;
  checked?: boolean;
}

export const SearchType = {
  FILTER: 'filter',
  SEARCH: 'search',
  PAGE_NUMBER: 'pageNumber',
  PAGE_SIZE: 'pageSize',
  SORT: 'sort',
};

export interface DashboardColumnSetting {
  index?: number;
  visible?: boolean;
  order?: any;
}

type SearchType_Keys = keyof typeof SearchType;

interface OwnProps {
  id: string;
  key?: string;
  className?: string;
  total?: number;
  leftActions?: ReactNode[];
  rightActions?: ReactNode[];
  /** section and its fields to be rendered in the filter drawer. this will take priority over fields prop */
  filterGroups?: Group[];
  /** the fields to be rendered in the filter drawer*/
  filterFields?: Filter[];
  searchPlaceholder?: string;
  /** set the filter drawer form fields by passing the form fields and its value as object */
  filters?: { [key: string]: any };
  /** filter keys to be ignored */
  ignoreFilters?: string[];
  /** filter keys to be excluded from count */
  filtersExcludeFromCount?: string[];
  searchVal?: string;
  searchWidth?: number | string;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  order?: string;
  onSearch?: (
    searchTerm: string,
    filters: any,
    pageNumber: number,
    pageSize: number,
    type: (typeof SearchType)[SearchType_Keys],
    sortBy?: string,
    order?: any,
  ) => void;
  onFilterClick?: () => void;
  tableColumnsRefresh?: (visibleColumns) => void;
  hideHeader?: boolean;
  hideSizeChanger?: boolean;
  noDataText?: string;
}

type DashboardTableProps = OwnProps & TableProps<any>;

function sortColumns(
  columns,
  tableCustomizationMap,
  tableCustomization,
): DashboardColumn[] {
  if (tableCustomizationMap) {
    let fixedLeftColumnsLength = 0;

    if (Array.isArray(tableCustomization)) {
      const fixedLeftColumns = columns.filter((col) => col.fixed === 'left');

      fixedLeftColumnsLength = fixedLeftColumns.length;
    }

    const columnsMapToArray = [];

    tableCustomizationMap.forEach((val, key) => {
      columnsMapToArray.push({
        ...val,
        key: key,
      });
    });

    columnsMapToArray.sort(
      (a, b) =>
        a.index + fixedLeftColumnsLength - (b.index - fixedLeftColumnsLength),
    );

    const updatedColumns = [];

    columnsMapToArray.forEach((val) => {
      const column = columns.find((col) => col.key === val.key);

      if (column) updatedColumns.push(column);
    });

    return updatedColumns;
  }

  return columns;
}

function LeftActionablesWrapper({ leftActions }) {
  if (!leftActions?.length) return <></>;

  return (
    <>
      <Divider
        type="vertical"
        style={{
          margin: 0,
          height: 16,
        }}
      />
      {leftActions.map((action) => action)}
    </>
  );
}

function RightActionablesWrapper({ rightActions }) {
  if (!rightActions?.length) return <></>;

  return <>{rightActions.map((action) => action)}</>;
}

function DashboardTitle({
  hideHeader,
  leftActions,
  searchWidth,
  searchValue,
  searchPlaceholder,
  loading,
  onSearch,
}) {
  if (hideHeader) return <></>;

  return (
    <Flex align="center" gap={16}>
      <TableSearch
        searchWidth={searchWidth}
        value={searchValue}
        placeholder={searchPlaceholder}
        loading={loading}
        onSearch={onSearch}
      />
      <LeftActionablesWrapper leftActions={leftActions} />
    </Flex>
  );
}

function FilterDrawerButton({
  id,
  appliedFilters,
  filterFields,
  filterGroups,
  onApply,
  ignoreFilters,
  filtersExcludeFromCount,
  onFilterClick,
}) {
  if (!filterFields.length && !filterGroups.length) return <></>;

  return (
    <FilterDrawer
      testId="dashboardTable-filterDrawer-btn"
      id={id}
      groups={filterGroups}
      fields={filterFields}
      onApply={onApply}
      filters={appliedFilters}
      ignoreFilters={ignoreFilters}
      filtersExcludeFromCount={filtersExcludeFromCount}
      onFilterClick={onFilterClick}
    />
  );
}

function ExtraWrapper({
  id,
  columns,
  hideHeader,
  filterFields,
  filterGroups,
  filtersExcludeFromCount,
  appliedFilters,
  ignoreFilters,
  rightActions,
  tableCustomizationMap,
  tableCustomization,
  onApply,
  onFilterClick,
  onTableCustomizerOk,
  onTableCustomizerRestoreDefault,
}) {
  const translateColumnsToTableCustomizerColumns = () => {
    const nextColumnsState = produce(columns, (_draft) => undefined);

    let tableCustomizerColumns = [];

    if (nextColumnsState)
      for (let i = 0; i < nextColumnsState.length; i += 1) {
        const column = nextColumnsState[i];

        if (column.fixed) continue;

        const columnKey = column.key.toString();

        tableCustomizerColumns.push({
          ...column,
          ...{
            checked:
              tableCustomizationMap.get(columnKey)?.visible ||
              !tableCustomizationMap.has(columnKey),
          },
        } as DashboardColumn);
      }

    tableCustomizerColumns = sortColumns(
      tableCustomizerColumns,
      tableCustomizationMap,
      tableCustomization,
    );

    return tableCustomizerColumns;
  };

  if (hideHeader) return <></>;

  return (
    <Flex align="center" gap={8}>
      <TableCustomizer
        testId="dashboardTable-tableCustomizer"
        columns={translateColumnsToTableCustomizerColumns()}
        onOk={onTableCustomizerOk}
        onRestoreDefault={onTableCustomizerRestoreDefault}
      />
      <FilterDrawerButton
        id={id}
        filterFields={filterFields}
        filterGroups={filterGroups}
        filtersExcludeFromCount={filtersExcludeFromCount}
        onApply={onApply}
        onFilterClick={onFilterClick}
        appliedFilters={appliedFilters}
        ignoreFilters={ignoreFilters}
      />
      <RightActionablesWrapper rightActions={rightActions} />
    </Flex>
  );
}

function DashboardTableWrapper({
  loading,
  dataSource,
  noDataText,
  rowKey,
  tableColumns,
  total,
  pageSize,
  hideSizeChanger,
  pageNumber,
  onChange,
  onRow,
  expandable,
}) {
  function getTableScrollX() {
    let x = 0;

    tableColumns.forEach((column) => {
      if (!column?.fixed && column?.width) {
        if (typeof column.width === 'number') {
          x += column.width;
          return;
        }

        if (column.width.endsWith('px')) {
          x += parseInt(column.width.replace('px', ''), 10);
          return;
        }

        if (column.width.endsWith('rem')) {
          x += parseInt(column.width.replace('rem', ''), 10) * 16;
          return;
        }
      }
    });

    return x;
  }

  if (loading)
    return (
      <div
        data-testid="dashboardTable-skeleton"
        style={{
          height: 'calc(100% - 16px)',
          overflow: 'hidden',
          padding: 16,
        }}
      >
        <Skeleton
          active
          paragraph={{
            rows: 42,
          }}
        />
      </div>
    );

  if (!dataSource?.length)
    return (
      <div
        data-testid="dashboardTable-empty"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: '#FFF',
        }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={noDataText} />
      </div>
    );

  return (
    <Table
      data-testid="dashboardTable-table"
      size="middle"
      style={{
        background: '#FFFFFF',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        cursor: 'pointer',
        height: '100%',
      }}
      rowKey={rowKey}
      rowClassName="exp-dashboard-v2"
      columns={tableColumns}
      dataSource={dataSource}
      pagination={{
        total: total,
        pageSize: pageSize,
        showSizeChanger: !hideSizeChanger,
        current: pageNumber,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        style: { alignItems: 'center' },
      }}
      scroll={{
        x: getTableScrollX(),
      }}
      onChange={onChange}
      onRow={onRow}
      expandable={expandable}
    />
  );
}

export default function DashboardTable({
  id,
  key,
  className = '',
  leftActions = [],
  rightActions = [],
  filterGroups = [],
  filterFields = [],
  searchPlaceholder,
  filters = {},
  ignoreFilters = [],
  filtersExcludeFromCount = [],
  searchVal = '',
  searchWidth,
  pageNo = 1,
  pageSize: inPageSize = VARIABLE_PAGE_SIZE,
  sortBy = '',
  order = null,
  total = 0,
  onSearch,
  onFilterClick = () => {},
  tableColumnsRefresh = (_visibleColumns) => {},
  hideHeader = false,
  hideSizeChanger = false,
  noDataText = 'No data',
  bordered,
  rowKey,
  columns,
  ...others
}: DashboardTableProps) {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(VARIABLE_PAGE_SIZE);
  const [appliedFilters, setAppliedFilters] = useState<{ [key: string]: any }>(
    {},
  );

  const [tableColumns, setTableColumns] = useState([]);
  const [searchValue, setSearchValue] = useState<string>();
  const [tableCustomization, setTableCustomization] = useLocalStorage(
    `${id}_tableCustomizer`,
    null,
  );
  const sorterRef = useRef(null);

  const debounceRefreshTable = debounce(() => {
    refreshTable();
  }, 300);

  useEffect(() => {
    debounceRefreshTable();
  }, [tableCustomization]);

  useEffect(() => {
    setSearchValue(searchVal);
  }, [searchVal]);

  useEffect(() => {
    if (!isEqual(filters, appliedFilters)) {
      setAppliedFilters(filters);
    }
  }, [filters]);

  useEffect(() => {
    setPageNumber(pageNo);
  }, [pageNo]);

  useEffect(() => {
    setPageSize(inPageSize);
  }, [inPageSize]);

  useEffect(() => {
    debounceRefreshTable();
  }, [sortBy, order]);

  const getTableCustomizationMap = (): Map<string, DashboardColumnSetting> => {
    const tableCustomizationMap = new Map();

    if (!tableCustomization && columns) {
      columns.map((column: DashboardColumn, index: number) => {
        tableCustomizationMap.set(column.key, {
          index,
          visible: !column.hidden,
          order: column.sortOrder,
        });
      });

      return tableCustomizationMap;
    }

    if (Array.isArray(tableCustomization)) {
      tableCustomization.forEach((customization, index: number) => {
        tableCustomizationMap.set(customization.key, {
          index,
          visible: customization.value,
          order: null,
        });
      });

      return tableCustomizationMap;
    }

    return new Map(JSON.parse(tableCustomization));
  };

  const getTableCustomizationMapWithSorterUpdate = (sorter) => {
    const tableCustomizationMap = getTableCustomizationMap();

    tableCustomizationMap.forEach((value) => {
      value.order = null;
    });

    const columnKey = sorter.columnKey;

    if (!tableCustomizationMap.has(columnKey)) {
      const index = columns.findIndex((col) => col.key === columnKey);
      const col: DashboardColumn = columns.find((col) => col.key === columnKey);

      tableCustomizationMap.set(columnKey, {
        index,
        visible: !col?.hidden,
        order: sorter.order,
      });
    } else tableCustomizationMap.get(columnKey).order = sorter.order;

    return tableCustomizationMap;
  };

  const handlePaginate = (pagination: TablePaginationConfig) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);

    onSearch(
      searchValue,
      appliedFilters,
      pagination.current,
      pagination.pageSize,
      pagination.current !== pageNumber
        ? SearchType.PAGE_NUMBER
        : SearchType.PAGE_SIZE,
      sorterRef.current?.columnKey,
      sorterRef.current?.order === 'ascend'
        ? 'asc'
        : sorterRef.current?.order === 'descend'
          ? 'desc'
          : null,
    );
  };

  const handleSorting = (sorter) => {
    setTableCustomization(
      JSON.stringify(
        Array.from(getTableCustomizationMapWithSorterUpdate(sorter).entries()),
      ),
    );

    sorterRef.current = !sorter.order ? null : sorter;

    onSearch(
      searchValue,
      appliedFilters,
      pageNumber,
      pageSize,
      SearchType.SORT,
      sorterRef.current?.columnKey,
      sorterRef.current?.order === 'ascend'
        ? 'asc'
        : sorterRef.current?.order === 'descend'
          ? 'desc'
          : null,
    );
  };

  const handlePageChange = (
    pagination: TablePaginationConfig,
    _filters,
    sorter,
    extra,
  ) => {
    const { action } = extra;

    switch (action) {
      case 'paginate':
        handlePaginate(pagination);
        break;
      case 'sort':
        handleSorting(sorter);
        break;
    }
  };

  const handleSearch = (searchValue) => {
    setPageNumber(1);
    setSearchValue(searchValue);

    onSearch(
      searchValue,
      appliedFilters,
      1,
      pageSize,
      SearchType.SEARCH,
      sorterRef.current?.columnKey,
      sorterRef.current?.order === 'ascend'
        ? 'asc'
        : sorterRef.current?.order === 'descend'
          ? 'desc'
          : null,
    );
  };

  const onFiltersApply = (filters: { [key: string]: any }) => {
    setAppliedFilters(filters);
    setPageNumber(1);

    onSearch(
      searchValue,
      filters,
      1,
      pageSize,
      SearchType.FILTER,
      sorterRef.current?.columnKey,
      sorterRef.current?.order === 'ascend'
        ? 'asc'
        : sorterRef.current?.order === 'descend'
          ? 'desc'
          : null,
    );
  };

  const setColumnSortOrder = (columns: TableColumnType<any>[]) => {
    const tableCustomizationMap: Map<string, DashboardColumnSetting> =
      getTableCustomizationMap();

    if (columns)
      columns.forEach((col: TableColumnType<any>) => {
        const columnKey = col.key.toString();

        if (sortBy) {
          if (sortBy === columnKey) {
            const sortOrder = order === 'desc' ? 'descend' : 'ascend';
            col.sortOrder = sortOrder;
          }

          return false;
        }

        if (tableCustomizationMap.has(columnKey))
          col.sortOrder = tableCustomizationMap.get(columnKey).order;
      });
  };

  const refreshTable = () => {
    const tableCustomizationMap: Map<string, DashboardColumnSetting> =
      getTableCustomizationMap();
    let columnsClone = lodash.cloneDeep(columns) || [];

    columnsClone = sortColumns(
      columnsClone,
      tableCustomizationMap,
      tableCustomization,
    );

    setColumnSortOrder(columnsClone);

    const visibleColumns: ColumnType<any>[] = [];

    columnsClone.forEach((column: DashboardColumn) => {
      const columnKey = column.key.toString();
      const { hidden, description, checked, ...others } = column;

      if (
        tableCustomizationMap.get(columnKey)?.visible ||
        !tableCustomizationMap.has(columnKey)
      ) {
        visibleColumns.push({
          ...others,
        });
      }
    });

    setTableColumns(visibleColumns);

    tableColumnsRefresh(visibleColumns);
  };

  const restoreTableDefault = () => {
    setTableCustomization(null);
  };

  function onTableCustomizerOK(
    tableSettingsMap: Map<string, DashboardColumnSetting>,
  ) {
    const tableCustomizationMap = getTableCustomizationMap();

    tableSettingsMap.forEach((value, key) => {
      tableCustomizationMap.get(key).index = value?.index;
      tableCustomizationMap.get(key).visible = value?.visible;
    });

    setTableCustomization(
      JSON.stringify(Array.from(tableCustomizationMap.entries())),
    );
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            headerHeight: 64,
          },
        },
      }}
    >
      <Card
        bordered={bordered}
        className={`${className} grab-x__listing`}
        style={{
          flexGrow: 1,
          flexShrink: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{
          body: {
            padding: 0,
            overflow: 'hidden',
          },
        }}
        size="default"
        title={
          hideHeader ? (
            false
          ) : (
            <DashboardTitle
              hideHeader={hideHeader}
              leftActions={leftActions}
              searchValue={searchValue}
              searchPlaceholder={searchPlaceholder}
              searchWidth={searchWidth}
              onSearch={handleSearch}
              loading={Boolean(others.loading)}
            />
          )
        }
        extra={
          hideHeader ? (
            false
          ) : (
            <ExtraWrapper
              id={id}
              columns={columns}
              filterFields={filterFields}
              filterGroups={filterGroups}
              filtersExcludeFromCount={filtersExcludeFromCount}
              ignoreFilters={ignoreFilters}
              appliedFilters={appliedFilters}
              hideHeader={hideHeader}
              tableCustomizationMap={getTableCustomizationMap()}
              tableCustomization={tableCustomization}
              rightActions={rightActions}
              onApply={onFiltersApply}
              onFilterClick={onFilterClick}
              onTableCustomizerOk={onTableCustomizerOK}
              onTableCustomizerRestoreDefault={restoreTableDefault}
            />
          )
        }
      >
        <DashboardTableWrapper
          loading={Boolean(others.loading)}
          dataSource={others.dataSource}
          noDataText={noDataText}
          rowKey={rowKey}
          tableColumns={tableColumns}
          onChange={handlePageChange}
          onRow={others.onRow}
          expandable={others.expandable}
          total={total}
          hideSizeChanger={hideSizeChanger}
          pageSize={pageSize}
          pageNumber={pageNumber}
        />
      </Card>
    </ConfigProvider>
  );
}
